import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';
import { useColor4AsPrimaryVars } from '../hooks/useCtaColorVars';

// ============================================================================
// Component
// ============================================================================

export function ScheduleTourForm() {
  const prefix = 'scheduletourform';
  const formBlockPath = `${prefix}.form_block`;

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: 'multihub-tour-scheduler',
  });
  const color4Vars = useColor4AsPrimaryVars();

  return (
    <>
      {/* Plain white section + centered scheduler */}
      <div className="stf-parallax-section">
        {/* Centered form */}
        <div className="stf-parallax-content">
          <div className="stf-form-wrap" style={color4Vars}>
            <EditableToken path={formBlockPath} as="div">
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : (
                <div className="stf-form-empty-card">
                  <p className="stf-form-empty">No form block selected</p>
                </div>
              )}
            </EditableToken>
          </div>
        </div>
      </div>

      {/* Schema-lint anchor — static path so the linter can match the block token.
          The live EditableToken above uses a dynamic path built from prefix. */}
      <EditableToken path="scheduletourform.form_block" as="div" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>

      <style>{`
        /* ── Section (plain white background) ── */
        .stf-parallax-section {
          position: relative;
          overflow: hidden;
          min-height: 0;
          padding: 1.25rem 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          background: white;
        }
        @media (min-width: 768px) {
          .stf-parallax-section {
            min-height: 520px;
            padding: 6rem 2rem;
          }
        }

        .stf-parallax-content {
          position: relative;
          z-index: 10;
          width: 100%;
          display: flex;
          justify-content: center;
        }

        .stf-form-wrap {
          width: 100%;
          max-width: 640px;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
        }
        /* Site typography: Space Mono body, Nunito Sans titles (loaded by the
           Header via the css2 link) and uppercase Oswald nav buttons. */
        .stf-form-wrap,
        .stf-form-wrap input,
        .stf-form-wrap select,
        .stf-form-wrap textarea {
          font-family: var(--vibe-font-body);
        }
        .stf-form-wrap h3,
        .stf-form-wrap h3 *,
        .stf-form-wrap h4 {
          font-family: 'Nunito Sans', sans-serif;
          font-weight: 400;
          text-transform: uppercase;
        }
        .stf-form-wrap h3 { letter-spacing: 0.02em; }
        .stf-form-wrap h4 { letter-spacing: 0.05em; }
        .stf-form-wrap .justify-between.mt-6 > button {
          font-family: var(--vibe-font-heading);
          font-weight: 400;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }

        .stf-form-empty-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem;
        }
        .stf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleTourForm',
  displayName: 'Schedule A Tour Form',
  tokens: {
    'scheduletourform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
    },
  },
};

(ScheduleTourForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleTourForm;
